// src/components/Tabs.js
import React from "react";

export default function Tabs({
  tabs,                 // [{ id, label, icon?: ReactNode, render: () => ReactNode }]
  defaultTabId,
  lazy = true,          // mount panels lazily
  keepAlive = false,    // keep previously-open panels mounted
  onChange,
  storageKey,           // e.g. "principal-dashboard.activeTab"
}) {
  const ids = tabs.map(t => t.id);
  const initial =
    (storageKey && window.localStorage.getItem(storageKey)) ||
    defaultTabId ||
    ids[0];

  const [activeId, setActiveId] = React.useState(() =>
    ids.includes(initial) ? initial : ids[0]
  );
  const visitedRef = React.useRef(new Set([activeId])); // for keepAlive/lazy

  const setActive = (id) => {
    if (!ids.includes(id)) return;
    if (storageKey) localStorage.setItem(storageKey, id);
    visitedRef.current.add(id);
    setActiveId(id);
    onChange?.(id);
  };

  // keyboard nav
  const onKeyDown = (e) => {
    const idx = ids.indexOf(activeId);
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setActive(ids[(idx + 1) % ids.length]);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setActive(ids[(idx - 1 + ids.length) % ids.length]);
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(ids[0]);
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(ids[ids.length - 1]);
    }
  };

  const activeIndex = ids.indexOf(activeId);

  return (
    <div className="gg-tabs" onKeyDown={onKeyDown}>
      <div className="gg-tablist" role="tablist" aria-label="Dashboard sections">
        {tabs.map((t, i) => {
          const selected = t.id === activeId;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={selected}
              aria-controls={`panel-${t.id}`}
              id={`tab-${t.id}`}
              className={`gg-tab ${selected ? "is-active" : ""}`}
              onClick={() => setActive(t.id)}
              tabIndex={selected ? 0 : -1}
            >
              {t.icon ? <span className="gg-tab__icon">{t.icon}</span> : null}
              <span className="gg-tab__label">{t.label}</span>
            </button>
          );
        })}
        {/* Animated underline */}
        <span
          className="gg-tab-underline"
          style={{
            transform: `translateX(${activeIndex * 100}%)`,
            width: `${100 / tabs.length}%`,
          }}
          aria-hidden="true"
        />
      </div>

      {/* Panels */}
      <div className="gg-panels">
        {tabs.map((t) => {
          const isActive = t.id === activeId;
          const show =
            isActive ||
            (!lazy) ||
            (keepAlive && visitedRef.current.has(t.id));

          if (!show) return null;

          return (
            <div
              key={t.id}
              role="tabpanel"
              id={`panel-${t.id}`}
              aria-labelledby={`tab-${t.id}`}
              className={`gg-panel ${isActive ? "is-active" : "is-hidden"}`}
            >
              {typeof t.render === "function" ? t.render() : t.render}
            </div>
          );
        })}
      </div>
    </div>
  );
}
